import { addDiscountConfig, loadCreditPlan } from 'apis/adminServices/adminDiscountService';
import React, { useEffect, useState } from 'react';
import { useToastStore } from 'store/toastStore';

interface CreditPlan {
  creditPlanId: number;
  name: string;
  credits: number;
}

interface AddDiscountModalProps {
  onClose: () => void;
  onDiscountAdded?: () => void;
}

const AddDiscountModal: React.FC<AddDiscountModalProps> = ({ onClose,onDiscountAdded }) => {
  const [creditPlanId, setCreditPlanId] = useState<number>(0);
  const [percentage, setPercentage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [creditPlanData, setCreditPlanData] = useState<CreditPlan[]>([]);
  const { showToast } = useToastStore();
  

  useEffect(() => {
    const fetchCreditPlan = async () => {
      try {
        const response = await loadCreditPlan();
        setCreditPlanData(response);
        // Set default to first plan if plans exist
        if (response.length > 0) {
          setCreditPlanId(response[0].creditPlanId);
        }
      } catch (error) {
        console.error('Error loading credit plan:', error);
        showToast('Failed to load credit plans', 'error', 3000);
      }
    };

    fetchCreditPlan();
  }, []);

  const handlePercentageChange = (value: string) => {
    // Remove non-digit characters
    const numericValue = value.replace(/\D/g, '');
    
    // Limit to 100 max
    const limitedValue = Math.min(Number(numericValue), 100).toString();
    
    setPercentage(limitedValue);
  };

  const handleAddDiscount = async () => {
    // Ensure percentage is not empty and is a valid number
    if (percentage.trim() === '') {
      showToast('Please enter a discount percentage', 'error', 3000);
      return;
    }

    setLoading(true);
    try {
      await addDiscountConfig(creditPlanId, Number(percentage));
      showToast('Discount added successfully', 'success', 3000);
      if (onDiscountAdded) {
        onDiscountAdded(); // Trigger API call in parent
      }
      onClose(); // Close modal on success
    } catch (error) {
      console.error('Error adding discount:', error);
      showToast('Failed to add discount', 'error', 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="relative p-4 w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-lg shadow-lg">
          <div className="flex justify-between p-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-800">Add Discount</h2>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6">
            {/* Credit Plan Dropdown */}
            <div className="mb-4">
              <label className="block text-xs font-light text-gray-500 mb-2">
                Credit Plan
              </label>
              <select
                value={creditPlanId}
                onChange={(e) => setCreditPlanId(Number(e.target.value))}
                className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5"
              >
                {creditPlanData.map((plan) => (
                  <option key={plan.creditPlanId} value={plan.creditPlanId}>
                    {plan.name} - {plan.credits} Credits
                  </option>
                ))}
              </select>
            </div>

            {/* Percentage Input */}
            <div className="mb-4">
              <label className="block text-xs font-light text-gray-500 mb-2">
                Discount Percentage (0-100%)
              </label>
              <input
                type="text"
                value={percentage}
                onChange={(e) => handlePercentageChange(e.target.value)}
                placeholder="Enter discount percentage"
                className="bg-blue-50 text-gray-600 text-xs rounded-md block w-full p-2.5"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={onClose}
                className="flex items-center gap-2 py-3 px-6 bg-gray-300 bg-opacity-20 rounded-md"
              >
                <p className="font-medium text-gray-500 text-xs">Cancel</p>
              </button>
              <button
                onClick={handleAddDiscount}
                className={`flex items-center gap-2 py-3 px-6 bg-[#FFD700] rounded-md text-white text-xs ${
                  loading || percentage.trim() === '' ? 'opacity-70 cursor-not-allowed' : ''
                }`}
                disabled={loading || percentage.trim() === ''}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDiscountModal;